import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { authService } from "@/services/auth";
import { handleApiError, messageFromApiErrorBody } from "@/services/api";
import { User, LoginCredentials, AuthState } from "@/types/auth";
import {
  clearStoredActiveOrganizationId,
  resolveActiveOrganizationId,
} from "@/utils/activeOrganization";
import { clearCsrfToken } from "@/utils/csrf";
import { useMockAuth, getMockUser, logMockModeWarning } from "@/mocks/mockData";

const getPasswordChangeErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return handleApiError(error);
  }
  if (error && typeof error === "object" && "response" in error) {
    const data = (error as { response?: { data?: unknown } }).response?.data;
    if (data) return messageFromApiErrorBody(data, "Password change failed");
  }
  return "Password change failed";
};

const getInitialState = (): AuthState => {
  if (useMockAuth) {
    logMockModeWarning();
    return {
      user: getMockUser() as User,
      isAuthenticated: true,
      sessionChecked: true,
      loading: false,
      error: null,
      activeOrganizationId: null,
    };
  }

  // Drop any pre-cookie-migration tokens left in localStorage.
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");

  return {
    user: null,
    isAuthenticated: false,
    sessionChecked: false,
    loading: false,
    error: null,
    activeOrganizationId: null,
  };
};

const initialState: AuthState = getInitialState();

export const login = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    if (useMockAuth) {
      return { user: getMockUser() as User };
    }

    try {
      return await authService.login(credentials);
    } catch (error: any) {
      const detail =
        error.response?.data?.message || error.response?.data?.detail || "Login failed";
      const detailText = typeof detail === "string" ? detail : "Login failed";
      if (/csrf/i.test(detailText)) {
        clearCsrfToken();
        return rejectWithValue("Could not start a secure login session. Refresh and try again.");
      }
      return rejectWithValue(detailText);
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async (_, { getState, rejectWithValue }) => {
  const state = getState() as { auth: AuthState };
  const userId = state.auth.user?.id;
  try {
    await authService.logout();
    clearStoredActiveOrganizationId(userId);
  } catch (error: any) {
    clearStoredActiveOrganizationId(userId);
    clearCsrfToken();
    return rejectWithValue(error.response?.data?.message || "Logout failed");
  }
});

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    if (useMockAuth) {
      return getMockUser() as User;
    }

    try {
      return await authService.getCurrentUser();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to get user");
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    data: { currentPassword: string; newPassword: string; confirmPassword: string },
    { rejectWithValue }
  ) => {
    try {
      await authService.changePassword(
        data.currentPassword,
        data.newPassword,
        data.confirmPassword
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(getPasswordChangeErrorMessage(error));
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.sessionChecked = true;
      state.loading = false;
      state.activeOrganizationId = resolveActiveOrganizationId(action.payload);
    },
    clearAuth: (state) => {
      clearStoredActiveOrganizationId(state.user?.id);
      clearCsrfToken();
      state.user = null;
      state.isAuthenticated = false;
      state.sessionChecked = true;
      state.error = null;
      state.activeOrganizationId = null;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setActiveOrganizationId: (state, action: PayloadAction<number | null>) => {
      state.activeOrganizationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.sessionChecked = true;
        state.user = action.payload.user;
        state.activeOrganizationId = resolveActiveOrganizationId(action.payload.user);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.sessionChecked = true;
        state.activeOrganizationId = null;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.sessionChecked = true;
        state.activeOrganizationId = null;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.sessionChecked = true;
        state.activeOrganizationId = resolveActiveOrganizationId(action.payload);
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = false;
        state.sessionChecked = true;
        state.user = null;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearAuth, clearError, setActiveOrganizationId } = authSlice.actions;
export default authSlice.reducer;
